import {
  buildOwnCaseManifestDraft,
  buildSingleCaseManifestDraft,
} from 'components/StudyDashboard/CaseManifest/dialogState';
import { getOrganizationEntryUrl } from 'components/StudyDashboard/CaseManifest/organizationEntry';
import { OrganizationReturnDescriptor } from 'components/StudyDashboard/CaseManifest/organizationTypes';
import {
  getFileInfoSlideIntakeEvidence,
  getSlideIntakeLifecycle,
  SlideIntakeLifecyclePresentation,
  SlideIntakeRequestState,
} from 'components/SlideIntake/lifecycle';
import { filter, map, size } from 'es-toolkit/compat';
import { FileInfo, UploadCategory, UploadDashboardRow } from 'interfaces/fileList';
import { Slide } from 'interfaces/slide';

export const uploadFilters = [
  'awaitingIngest',
  'missingCaseAssignment',
  'assignedToCase',
  'manifest',
  'other',
  'hidden',
] as const;

export type UploadFilter = (typeof uploadFilters)[number];

export const uploadFilterLabels: Record<UploadFilter, string> = {
  awaitingIngest: 'Awaiting ingest',
  assignedToCase: 'Assigned to case',
  missingCaseAssignment: 'Needs case assignment',
  manifest: 'Manifest files',
  other: 'Other files',
  hidden: 'Hidden',
};

export const buildUploadCategoryRows = (rows: FileInfo[], uploadCategory: UploadCategory): UploadDashboardRow[] => {
  return map(rows, (row) => ({
    ...row,
    uploadCategory,
  }));
};

export const hasCaseAssignment = (row: FileInfo) => size(row.cases || []) > 0;

export const getUploadDashboardLifecycle = (
  row: UploadDashboardRow,
  requestState: SlideIntakeRequestState = 'ready'
) => {
  const fileKind = row.isManifest ? 'manifest' : row.uploadKey || row.slideId ? 'slide' : 'unsupported';

  return getSlideIntakeLifecycle(getFileInfoSlideIntakeEvidence(row, requestState, fileKind));
};

export const getUploadFilterForLifecycle = (lifecycle: SlideIntakeLifecyclePresentation): UploadFilter => {
  switch (lifecycle.state) {
    case 'hidden':
      return 'hidden';
    case 'manifestFile':
      return 'manifest';
    case 'unsupportedFile':
      return 'other';
    case 'assignedToCase':
      return 'assignedToCase';
    case 'needsCaseAssignment':
      return 'missingCaseAssignment';
    default:
      return 'awaitingIngest';
  }
};

export const getUploadDashboardFilter = (row: UploadDashboardRow): UploadFilter =>
  getUploadFilterForLifecycle(getUploadDashboardLifecycle(row));

export const getUploadRowsForFilter = (rows: UploadDashboardRow[], uploadFilter: UploadFilter) =>
  map(
    filter(rows, (row) => getUploadDashboardFilter(row) === uploadFilter),
    (row) => ({ ...row, uploadCategory: uploadFilter })
  );

export const isUploadIngestActionAvailable = (
  row: UploadDashboardRow,
  lifecycle: SlideIntakeLifecyclePresentation,
  canReingestUploadedSlides: boolean
) =>
  Boolean(row.uploadKey) &&
  (lifecycle.actionCategory === 'trackIngest' ||
    lifecycle.actionCategory === 'retryIngest' ||
    (canReingestUploadedSlides &&
      (lifecycle.actionCategory === 'organizeSlides' || lifecycle.actionCategory === 'openAssignedCase')));

const buildManifestSlidesFromUploadRows = ({
  rows,
  labId,
  studyId,
}: {
  rows: UploadDashboardRow[];
  labId: string;
  studyId: string;
}): Slide[] =>
  map(rows, (row) => ({
    id: row.slideId || '',
    labId,
    studyId,
    sourceStudyId: studyId,
    originalFileName: row.fileName || row.name,
    createdAt: row.date,
  }));

export const getManifestDialogUrlForUploads = ({
  uploadRows,
  groupIntoSingleCase,
  labId,
  studyId,
  returnTo,
}: {
  uploadRows: UploadDashboardRow[];
  groupIntoSingleCase: boolean;
  labId: string;
  studyId: string;
  returnTo?: OrganizationReturnDescriptor;
}) => {
  const uploadRowsWithSlideIds = filter(uploadRows, (row) => Boolean(row.slideId));
  const uploadRowsWithoutSlideIds = filter(uploadRows, (row) => !row.slideId);
  const manifestSlides = buildManifestSlidesFromUploadRows({ rows: uploadRowsWithoutSlideIds, labId, studyId });
  const manualManifestSlideIdsToLoad = map(uploadRowsWithSlideIds, (row) => String(row.slideId));

  if (size(manifestSlides) === 0 && size(manualManifestSlideIdsToLoad) === 0) {
    return null;
  }

  const draftCases = groupIntoSingleCase
    ? buildSingleCaseManifestDraft({ slides: manifestSlides, labId })
    : buildOwnCaseManifestDraft({ slides: manifestSlides, labId });

  return getOrganizationEntryUrl({
    studyId,
    labId,
    returnTo: returnTo || { origin: 'uploads', labId, studyId },
    manifestState: {
      draftCases,
      manualManifestSlideIdsToLoad,
      manualManifestGroupIntoSingleCase: groupIntoSingleCase,
    },
  });
};
